import React, { useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScoreBreakdown } from '@/types/survey';
import { useLanguage } from '@/contexts/LanguageContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Info, HelpCircle, TrendingUp } from 'lucide-react';
import * as THREE from 'three';

interface PredictionViewProps {
  scoreBreakdown: ScoreBreakdown | null;
}

// Constants for the visualization with improved color scheme
const CHART_COLORS = {
  beam: '#F97316',  // orange for BEAM Score
  nev: '#3b82f6',   // blue for NEV
  eroi: '#22c55e',  // green for EROI
  background: '#f0f9ff', // light background
  gridLines: '#e5e7eb', // lighter gray for grid
  labels: '#374151',    // dark gray for labels
};

// Simplified 3D line component
const MetricLine = ({ points, color, currentValue, futureValue }) => {
  const lineRef = useRef();
  const [hovered, setHovered] = useState(false);
  
  // Create a curve from the points
  const curve = new THREE.CatmullRomCurve3(
    points.map(point => new THREE.Vector3(...point))
  );
  
  const points3D = curve.getPoints(50);
  
  return (
    <group>
      <line ref={lineRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={points3D.length}
            array={new Float32Array(points3D.flatMap(p => [p.x, p.y, p.z]))}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={color} linewidth={2} />
      </line>
      
      {/* Points along the line */}
      {[points[0], points[points.length - 1]].map((point, i) => (
        <mesh 
          key={i} 
          position={[point[0], point[1], point[2]]}
          onPointerOver={() => setHovered(true)}
          onPointerOut={() => setHovered(false)}
        >
          <sphereGeometry args={[0.1, 16, 16]} />
          <meshStandardMaterial color={color} />
        </mesh>
      ))}
      
      {/* Start and end labels */}
      {points.length > 0 && (
        <>
          {/* Current value label */}
          <group position={[points[0][0], points[0][1] - 0.3, points[0][2]]}>
            <mesh>
              <planeGeometry args={[0.8, 0.3]} />
              <meshBasicMaterial color="#ffffff" transparent opacity={0.7} />
            </mesh>
            {/* Simple text representation */}
            <Text 
              position={[0, 0, 0.01]} 
              color={color}
              fontSize={0.1}
            >
              {currentValue.toFixed(1)}
            </Text>
          </group>
          
          {/* Future value label */}
          <group position={[points[points.length - 1][0], points[points.length - 1][1] + 0.3, points[points.length - 1][2]]}>
            <mesh>
              <planeGeometry args={[0.8, 0.3]} />
              <meshBasicMaterial color="#ffffff" transparent opacity={0.7} />
            </mesh>
            {/* Simple text representation */}
            <Text 
              position={[0, 0, 0.01]} 
              color={color}
              fontSize={0.1}
            >
              {futureValue.toFixed(1)}
            </Text>
          </group>
        </>
      )}
    </group>
  );
};

// Simplified chart scene
const MetricChart = ({ currentValue, potentialValue, color, maxValue, title }) => {
  // Generate points for the line progression
  const points = [
    [-1.5, currentValue / (maxValue / 2), 0],  // Current point
    [-0.5, (currentValue + potentialValue) / 2 / (maxValue / 2), 0.2],  // Control point
    [0.5, (currentValue + potentialValue) / 2 / (maxValue / 2) * 1.1, -0.2],  // Control point
    [1.5, potentialValue / (maxValue / 2), 0]   // Future point
  ];

  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[5, 5, 5]} intensity={0.8} />
      
      {/* Grid */}
      <gridHelper 
        args={[5, 10]} 
        position={[0, 0, 0]} 
        rotation={[Math.PI/2, 0, 0]} 
      />
      
      {/* Line */}
      <MetricLine 
        points={points} 
        color={color} 
        currentValue={currentValue} 
        futureValue={potentialValue} 
      />
      
      {/* Grid scale markers */}
      {Array.from({ length: Math.ceil(maxValue) + 1 }).map((_, i) => {
        if (i % Math.ceil(maxValue / 5) !== 0) return null;
        const y = i / (maxValue / 2);
        return (
          <group key={`marker-${i}`} position={[-2, y, 0]}>
            <mesh scale={[0.1, 0.01, 0.01]}>
              <boxGeometry />
              <meshBasicMaterial color="#999999" />
            </mesh>
          </group>
        );
      })}
      
      {/* Title */}
      <group position={[0, 2.5, 0]}>
        <Text
          color="#333333"
          fontSize={0.15}
        >
          {title}
        </Text>
      </group>
    </>
  );
};

// Fallback 2D chart for when 3D rendering fails
const FallbackChart = ({ currentValue, potentialValue, color, maxValue, label }) => {
  const barHeight = 20;
  const percentCurrent = (currentValue / maxValue) * 100;
  const percentPotential = (potentialValue / maxValue) * 100;
  
  return (
    <div className="p-4 bg-white rounded-lg border border-gray-200">
      <div className="mb-2 flex justify-between">
        <span className="text-sm font-medium">{label} Current</span>
        <span className="text-sm font-medium" style={{ color }}>{currentValue.toFixed(1)}</span>
      </div>
      <div className="w-full bg-gray-200 h-5 rounded-full mb-4">
        <div 
          className="h-5 rounded-full" 
          style={{ width: `${percentCurrent}%`, backgroundColor: color }}
        ></div>
      </div>
      
      <div className="mb-2 flex justify-between">
        <span className="text-sm font-medium">{label} Potential</span>
        <span className="text-sm font-medium" style={{ color }}>{potentialValue.toFixed(1)}</span>
      </div>
      <div className="w-full bg-gray-200 h-5 rounded-full">
        <div 
          className="h-5 rounded-full" 
          style={{ width: `${percentPotential}%`, backgroundColor: color }}
        ></div>
      </div>
      
      <div className="mt-2 text-xs text-gray-500 flex justify-end">
        <span>Improvement: +{(potentialValue - currentValue).toFixed(1)} ({((potentialValue - currentValue) / currentValue * 100).toFixed(0)}%)</span>
      </div>
    </div>
  );
};

const PredictionView: React.FC<PredictionViewProps> = ({ scoreBreakdown }) => {
  const { t } = useLanguage();
  const [activeMetricTab, setActiveMetricTab] = React.useState('beam');
  const [hoveredTimeline, setHoveredTimeline] = React.useState<string | null>(null);
  const [renderMode, setRenderMode] = React.useState<'3d' | '2d'>('2d'); // Default to 2D for safety
  const [errorLoading, setErrorLoading] = React.useState(false);

  React.useEffect(() => {
    // Check if WebGL is supported
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) {
        console.log('WebGL not supported, using 2D mode');
      } else {
        setRenderMode('2d'); // Still default to 2D even if WebGL is supported for stability
      }
    } catch (e) {
      console.log('Error detecting WebGL support:', e);
    }
  }, []);

  if (!scoreBreakdown) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('ai.prediction.future')}</CardTitle>
          <CardDescription>No data available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Create data for the potential improvement chart
  // Use different scales for each metric
  const currentBeamScore = scoreBreakdown.overallScore ? scoreBreakdown.overallScore * 10 : 0;
  const potentialBeamScore = Math.min(100, currentBeamScore * 1.2); // Max 100
  
  const currentNEV = scoreBreakdown.nev || scoreBreakdown.totalNEV || 0;
  const potentialNEV = Math.min(50, currentNEV * 1.3); // Max 50
  
  const currentEROI = scoreBreakdown.eroi || (currentNEV / 10) || 0;
  const potentialEROI = Math.min(5, currentEROI * 1.4); // Max 5

  // Focus areas based on the metrics that need the most improvement
  const focusAreas = [
    scoreBreakdown.ee && scoreBreakdown.ee.percentage < 70 ? 'Emotional Attachment' : null,
    scoreBreakdown.lq && scoreBreakdown.lq.percentage < 60 ? 'Legacy Value' : null,
    scoreBreakdown.fc && scoreBreakdown.fc.percentage > 50 ? 'Reducing Friction' : null,
  ].filter(Boolean);

  const timelineMarkers = [
    { position: 1/4, label: '1 month', description: 'Initial improvements', color: '#3b82f6' },
    { position: 2/4, label: '3 months', description: 'Significant progress', color: '#8b5cf6' },
    { position: 3/4, label: '6 months', description: 'Target achievement', color: '#22c55e' }
  ];

  const handleError = () => {
    console.error("Error loading 3D visualization");
    setErrorLoading(true);
    setRenderMode('2d');
  };

  // Render the chart based on the selected mode
  const renderChart = (metricType) => {
    let currentValue, potentialValue, maxValue, color, label;
    
    switch (metricType) {
      case 'beam':
        currentValue = currentBeamScore;
        potentialValue = potentialBeamScore;
        maxValue = 100;
        color = CHART_COLORS.beam;
        label = 'BEAM Score';
        break;
      case 'nev':
        currentValue = currentNEV;
        potentialValue = potentialNEV;
        maxValue = 50;
        color = CHART_COLORS.nev;
        label = 'NEV';
        break;
      case 'eroi':
        currentValue = currentEROI;
        potentialValue = potentialEROI;
        maxValue = 5;
        color = CHART_COLORS.eroi;
        label = 'EROI';
        break;
      default:
        currentValue = currentBeamScore;
        potentialValue = potentialBeamScore;
        maxValue = 100;
        color = CHART_COLORS.beam;
        label = 'BEAM Score';
    }
    
    // Always render the 2D fallback for reliability
    return (
      <FallbackChart 
        currentValue={currentValue} 
        potentialValue={potentialValue} 
        color={color} 
        maxValue={maxValue}
        label={label}
      />
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            {t('ai.prediction.future')}
            <Popover>
              <PopoverTrigger asChild>
                <button className="ml-2">
                  <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <p className="text-sm text-gray-600">
                  These projections show how your metrics could improve over time with focused effort on the recommended areas.
                </p>
              </PopoverContent>
            </Popover>
          </CardTitle>
          <CardDescription>
            {t('ai.prediction.note')} - {t('ai.prediction.based.on')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeMetricTab} onValueChange={setActiveMetricTab}>
            <TabsList className="mb-4 grid w-full grid-cols-3">
              <TabsTrigger value="beam" className="flex items-center justify-center">
                <span className="w-3 h-3 rounded-full bg-orange-500 mr-2"></span>
                BEAM Score (0-100)
              </TabsTrigger>
              <TabsTrigger value="nev" className="flex items-center justify-center">
                <span className="w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                NEV (0-50)
              </TabsTrigger>
              <TabsTrigger value="eroi" className="flex items-center justify-center">
                <span className="w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                EROI (0-5)
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="beam">
              {renderChart('beam')}
              <div className="mt-4 flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">Current: </span>
                  <span className="text-orange-600 font-semibold">{currentBeamScore.toFixed(2)}</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Potential Improvement: </span>
                  <span className="text-green-600 font-semibold">+{(potentialBeamScore - currentBeamScore).toFixed(2)} {t('ai.prediction.points')}</span>
                  <span className="text-green-600 ml-1 text-xs">(↑{((potentialBeamScore - currentBeamScore) / currentBeamScore * 100).toFixed(0)}%)</span>
                </div>
                <div className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">Target: </span>
                  <span className="text-orange-600 font-semibold">{potentialBeamScore.toFixed(2)}</span>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="nev">
              {renderChart('nev')}
              <div className="mt-4 flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">Current: </span>
                  <span className="text-blue-600 font-semibold">{currentNEV.toFixed(2)}</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Potential Improvement: </span>
                  <span className="text-green-600 font-semibold">+{(potentialNEV - currentNEV).toFixed(2)} {t('ai.prediction.points')}</span>
                  <span className="text-green-600 ml-1 text-xs">(↑{((potentialNEV - currentNEV) / currentNEV * 100).toFixed(0)}%)</span>
                </div>
                <div className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">Target: </span>
                  <span className="text-blue-600 font-semibold">{potentialNEV.toFixed(2)}</span>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="eroi">
              {renderChart('eroi')}
              <div className="mt-4 flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">Current: </span>
                  <span className="text-green-600 font-semibold">{currentEROI.toFixed(2)}x</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Potential Improvement: </span>
                  <span className="text-green-600 font-semibold">+{(potentialEROI - currentEROI).toFixed(2)}x</span>
                  <span className="text-green-600 ml-1 text-xs">(↑{((potentialEROI - currentEROI) / currentEROI * 100).toFixed(0)}%)</span>
                </div>
                <div className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">Target: </span>
                  <span className="text-green-600 font-semibold">{potentialEROI.toFixed(2)}x</span>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="mt-8">
            <h3 className="font-semibold text-gray-700 mb-2">{t('ai.prediction.timeline')}</h3>
            <div className="bg-gray-50 rounded-lg p-4 shadow-inner">
              <div className="relative">
                <div className="h-2 bg-gray-200 rounded-full w-full relative">
                  {timelineMarkers.map((marker, index) => (
                    <div 
                      key={index} 
                      className="absolute h-4 w-4 rounded-full cursor-pointer transform -translate-y-1/2"
                      style={{ 
                        left: `${marker.position * 100}%`, 
                        backgroundColor: marker.color,
                        top: '50%'
                      }}
                      onMouseEnter={() => setHoveredTimeline(marker.label)}
                      onMouseLeave={() => setHoveredTimeline(null)}
                    />
                  ))}
                </div>
                
                <div className="flex justify-between mt-4">
                  {timelineMarkers.map((marker, index) => (
                    <div 
                      key={index} 
                      className="text-xs text-gray-600 relative"
                      style={{ 
                        left: index === 0 ? '0' : (index === timelineMarkers.length - 1 ? '0' : '0'),
                        width: '33%',
                        textAlign: index === 0 ? 'left' : (index === timelineMarkers.length - 1 ? 'right' : 'center')
                      }}
                    >
                      <div className="font-medium">{marker.label}</div>
                      {hoveredTimeline === marker.label && (
                        <div className="absolute top-6 left-0 bg-white p-2 shadow-lg rounded-md z-10 w-48">
                          <div className="text-sm font-medium">{marker.description}</div>
                          <div className="mt-1 text-xs">
                            Expected BEAM: <span className="font-medium">{(currentBeamScore + (potentialBeamScore - currentBeamScore) * marker.position).toFixed(1)}</span>
                          </div>
                          <div className="text-xs">
                            Expected NEV: <span className="font-medium">{(currentNEV + (potentialNEV - currentNEV) * marker.position).toFixed(1)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8">
            <h3 className="font-semibold text-gray-700 mb-2">{t('ai.prediction.focus.areas')}</h3>
            <ul className="list-disc pl-5 space-y-2 bg-gray-50 p-4 rounded-lg">
              {focusAreas.map((area, index) => (
                <li key={index} className="text-gray-700">{area}</li>
              ))}
              {focusAreas.length === 0 && (
                <li className="text-gray-700">Maintaining current performance</li>
              )}
            </ul>
          </div>
          
          <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-100">
            <div className="flex items-start">
              <div className="mr-3 mt-1">
                <Info className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <h4 className="font-medium text-blue-800 mb-1">Performance Projections</h4>
                <p className="text-sm text-blue-700">
                  Due to compatibility issues, we're showing 2D projections for your metrics. These visualize your potential growth based on industry benchmarks.
                </p>
              </div>
            </div>
          </div>
          
          <Popover>
            <PopoverTrigger asChild>
              <button className="mt-2 text-xs text-blue-600 flex items-center hover:underline">
                <Info className="h-3 w-3 mr-1" /> How are these projections calculated?
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <p className="text-xs text-gray-600">
                Projections are based on industry benchmarks and potential improvements in your core metrics. BEAM Score potential assumes 20% improvement in emotional metrics, NEV assumes 30% improvement through better engagement, and EROI assumes 40% improvement in efficiency metrics.
              </p>
            </PopoverContent>
          </Popover>
        </CardContent>
      </Card>
    </div>
  );
};

export default PredictionView;
